import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { requests, send } from "../api/auth";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { resolveLocale } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import { browserStore, forgetAll } from "../session/lastKnown";

// ---------------------------------------------------------------------------
// TOUR T-22 (owner, 21 October 2026): YOUR ACCOUNT.
//
// What used to crowd the header: the language, and "Sign out everywhere". The
// picker shows the language the console is speaking right now (the person's own
// choice, else the organisation's country's), and choosing stores it on the
// account. Signing out of every device is a deliberate act, so it is here, with
// its words, and not a second button beside "Sign out".
// ---------------------------------------------------------------------------
export function Account() {
  const { apiBase, me, reload } = useSession();
  const { t } = useT();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: "live" | "failed"; status: string; message: string } | null>(null);
  const [name, setName] = useState("");
  const ownName = me?.authenticated ? (me.display_name ?? "") : "";
  useEffect(() => setName(ownName), [ownName]);
  if (!me?.authenticated) return null;
  const active = resolveLocale(me.locale, me.active_org?.country);

  async function chooseLanguage(value: string) {
    setBusy(true);
    setNotice(null);
    try {
      const { status } = await send(requests.setLocale(apiBase, value));
      if (status !== 200) setNotice({ tone: "failed", status: t("Not saved"), message: t("The language could not be saved.") });
      else await reload();
    } catch {
      setNotice({ tone: "failed", status: t("Not saved"), message: t("We could not reach Verbunk. Check your connection and try again.") });
    } finally {
      setBusy(false);
    }
  }

  async function saveName() {
    setBusy(true);
    setNotice(null);
    try {
      const { status } = await send(requests.setName(apiBase, name.trim() === "" ? null : name.trim()));
      if (status !== 200) setNotice({ tone: "failed", status: t("Not saved"), message: t("Your name could not be saved.") });
      else {
        await reload();
        setNotice({ tone: "live", status: t("Saved"), message: t("Your name is saved.") });
      }
    } catch {
      setNotice({ tone: "failed", status: t("Not saved"), message: t("We could not reach Verbunk. Check your connection and try again.") });
    } finally {
      setBusy(false);
    }
  }

  async function signOutEverywhere() {
    setBusy(true);
    setNotice(null);
    try {
      // NOT best effort: somebody who asked for the lost phone to be shut out must not be
      // told it was when it was not.
      const { status } = await send(requests.logoutAll(apiBase));
      if (status !== 200) {
        setNotice({ tone: "failed", status: t("Not signed out"), message: t("Your other devices are still signed in. Try again.") });
        return;
      }
      forgetAll(browserStore());
      await reload();
      navigate("/sign-in", { replace: true });
    } catch {
      setNotice({ tone: "failed", status: t("Not signed out"), message: t("Your other devices are still signed in. Try again.") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <h1>{t("Your account")}</h1>

      <div className="form-message" role="status" aria-live="polite">
        {notice ? (
          <>
            <StatusChip tone={notice.tone} label={notice.status} />
            <span>{notice.message}</span>
          </>
        ) : null}
      </div>

      <section className="panel">
        <div className="panel-head">
          <h2>{t("Your name")}</h2>
        </div>
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            void saveName();
          }}
        >
          <div className="field">
            <label htmlFor="account-name">{t("Name")}</label>
            <input id="account-name" value={name} maxLength={120} autoComplete="name" onChange={(e) => setName(e.target.value)} />
            <p className="field-hint">
              {t("Optional. Your organisation may show it in lists of people; if it does not, only your email is shown. You can change or remove it at any time.")}
            </p>
          </div>
          <div className="row">
            <button type="submit" className="btn" disabled={busy || name.trim() === ownName}>
              {t("Save")}
            </button>
          </div>
        </form>
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2>{t("Language")}</h2>
        </div>
        <div className="field">
          <label htmlFor="account-language">{t("The console speaks")}</label>
          <select id="account-language" value={active} disabled={busy} onChange={(e) => void chooseLanguage(e.target.value)}>
            <option value="en">English</option>
            <option value="hu" lang="hu">
              Magyar
            </option>
          </select>
          <p className="field-hint">{t("Your own choice, kept on your account. Each screen and email follows it.")}</p>
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2>{t("Signed in on other devices")}</h2>
        </div>
        <p className="muted">
          {t("Signing out here ends this device only. If a phone or a shared computer was left signed in, end every session at once.")}
        </p>
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => void signOutEverywhere()}>
          {t("Sign out everywhere")}
        </button>
      </section>
    </div>
  );
}
